/**
 * Charts library — ported from shared/js/charts.js
 * Uses global uPlot loaded via <script> tag.
 */

const uPlot = (window as any).uPlot;

// ── Shared cursor sync group ──────────────────────────────────────────
let _cursorSync: any = null;
function getCursorSync() {
  if (!_cursorSync && uPlot) _cursorSync = uPlot.sync('telemetry');
  return _cursorSync;
}

// ── Color palette ─────────────────────────────────────────────────────
export const CHART_COLORS = {
  speed:        '#34d399',
  acceleration: '#fb923c',
  rpm:          '#fbbf24',
  gforceX:      '#f87171',
  gforceY:      '#60a5fa',
  grid:         'rgba(255,255,255,0.04)',
  axis:         'rgba(255,255,255,0.25)',
  tick:         '#55556a',
  cursor:       'rgba(124,92,252,0.5)',
  sectorBand:   'rgba(255,255,255,0.03)',
};

// ── Helpers ───────────────────────────────────────────────────────────

/** Convert hex colour + alpha to a CSS rgba fill string */
export function hexToFill(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Track-cursor link plugin ──────────────────────────────────────────

export interface TrackMapApi {
  setHighlight(idx: number): void;
  setRange(min: number, max: number): void;
}

function cursorTrackPlugin(trackMapApi: TrackMapApi | null) {
  if (!trackMapApi) return {};
  return {
    hooks: {
      setCursor: [
        (u: any) => {
          const { idx } = u.cursor;
          if (idx == null) {
            trackMapApi.setHighlight(-1);
          } else {
            trackMapApi.setHighlight(idx);
          }
        },
      ],
    },
  };
}

// ── Pinned point plugin (track map click → chart marker) ──────────────

export interface PinnedPointHandle {
  plugin: any;
  setPinned(idx: number, chart: any): void;
}

export interface PinnedPointPluginOptions {
  /** Called when the user drags on the chart to scrub the pinned point. */
  onIndexChange?: (sampleIdx: number) => void;
}

export function createPinnedPointPlugin(options?: PinnedPointPluginOptions): PinnedPointHandle {
  let pinnedIdx = -1;
  let _isDragging = false;
  const clearSel = { left: 0, width: 0, top: 0, height: 0 };

  const plugin = {
    hooks: {
      ready: [
        (u: any) => {
          const over = u.over as HTMLElement;
          // Prevent page scroll while dragging on the chart (mobile)
          over.style.touchAction = 'none';

          over.addEventListener('pointerdown', (e: PointerEvent) => {
            _isDragging = true;
            over.setPointerCapture(e.pointerId);
            // Immediately pin at the current cursor position
            if (u.cursor.idx != null) {
              pinnedIdx = u.cursor.idx;
              options?.onIndexChange?.(pinnedIdx);
              u.setSelect(clearSel, false);
              u.redraw();
            }
          });

          over.addEventListener('pointerup', (e: PointerEvent) => {
            if (_isDragging) {
              _isDragging = false;
              over.releasePointerCapture(e.pointerId);
            }
          });

          over.addEventListener('pointercancel', (e: PointerEvent) => {
            if (_isDragging) {
              _isDragging = false;
              over.releasePointerCapture(e.pointerId);
            }
          });
        },
      ],
      setCursor: [
        (u: any) => {
          if (_isDragging && u.cursor.idx != null && u.cursor.idx !== pinnedIdx) {
            pinnedIdx = u.cursor.idx;
            options?.onIndexChange?.(pinnedIdx);
            // Clear uPlot's zoom selection so it doesn't interfere with drag
            u.setSelect(clearSel, false);
            u.redraw();
          }
        },
      ],
      draw: [
        (u: any) => {
          if (pinnedIdx < 0) return;
          const time = pinnedIdx * 0.1;
          const ctx = u.ctx;
          const { left, top, width, height } = u.bbox;
          const xPos = u.valToPos(time, 'x', true);

          if (xPos < left || xPos > left + width) return;

          // Vertical dashed line
          ctx.save();
          ctx.beginPath();
          ctx.setLineDash([4, 3]);
          ctx.strokeStyle = 'rgba(124, 92, 252, 0.6)';
          ctx.lineWidth = 1.5;
          ctx.moveTo(xPos, top);
          ctx.lineTo(xPos, top + height);
          ctx.stroke();
          ctx.setLineDash([]);

          // Dot + value label at each data series value
          for (let si = 1; si < u.series.length; si++) {
            if (!u.series[si].show) continue;
            const yVal = u.data[si][pinnedIdx];
            if (yVal == null) continue;
            const scaleKey = u.series[si].scale || 'y';
            const yPos = u.valToPos(yVal, scaleKey, true);

            // Outer glow
            ctx.beginPath();
            ctx.arc(xPos, yPos, 7, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(124, 92, 252, 0.2)';
            ctx.fill();
            // Accent circle
            ctx.beginPath();
            ctx.arc(xPos, yPos, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#7c5cfc';
            ctx.fill();
            // White center
            ctx.beginPath();
            ctx.arc(xPos, yPos, 1.8, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();

            // Value label
            const seriesColor = u.series[si].stroke || '#7c5cfc';
            const label = typeof yVal === 'number' ? (Math.abs(yVal) < 10 ? yVal.toFixed(2) : yVal.toFixed(1)) : String(yVal);
            ctx.font = '600 10px Inter, sans-serif';
            const textW = ctx.measureText(label).width;
            const padH = 5;
            const pillW = textW + padH * 2;
            const pillH = 14;

            // Position label to the right of dot, or to the left if near the right edge
            const rightEdge = left + width;
            let labelX = xPos + 10;
            if (labelX + pillW > rightEdge - 4) {
              labelX = xPos - 10 - pillW;
            }
            const labelY = yPos - pillH / 2;

            // Pill background
            ctx.beginPath();
            ctx.roundRect(labelX, labelY, pillW, pillH, 4);
            ctx.fillStyle = 'rgba(10, 10, 15, 0.85)';
            ctx.fill();
            ctx.strokeStyle = typeof seriesColor === 'string' ? seriesColor : '#7c5cfc';
            ctx.lineWidth = 1;
            ctx.stroke();

            // Label text
            ctx.fillStyle = '#e2e2f0';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(label, labelX + padH, labelY + pillH / 2);
          }

          // Time label at the bottom of the vertical line
          const timeVal = pinnedIdx * 0.1;
          const timeLabel = timeVal.toFixed(1) + 's';
          ctx.font = '600 9px Inter, sans-serif';
          const timeLabelW = ctx.measureText(timeLabel).width;
          const timePadH = 4;
          const timePillW = timeLabelW + timePadH * 2;
          const timePillH = 13;
          let timeLabelX = xPos - timePillW / 2;
          // Clamp to chart area
          if (timeLabelX < left) timeLabelX = left;
          if (timeLabelX + timePillW > left + width) timeLabelX = left + width - timePillW;
          const timeLabelY = top + height + 2;

          ctx.beginPath();
          ctx.roundRect(timeLabelX, timeLabelY, timePillW, timePillH, 3);
          ctx.fillStyle = 'rgba(124, 92, 252, 0.9)';
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillText(timeLabel, timeLabelX + timePadH, timeLabelY + timePillH / 2);

          ctx.restore();
        },
      ],
    },
  };

  return {
    plugin,
    setPinned(idx: number, chart: any) {
      pinnedIdx = idx;
      if (chart) {
        try {
          // Move uPlot's cursor to the pinned position so the built-in legend
          // updates with the values at that index.
          const time = idx * 0.1;
          const left = chart.valToPos(time, 'x');
          if (isFinite(left)) {
            chart.setCursor({ left, top: 0 });
          }
          chart.redraw();
        } catch (_) {
          // Chart may have been destroyed between the ref check and here
        }
      }
    },
  };
}

// ── Zoom sync plugin ──────────────────────────────────────────────────

export interface ZoomPluginOpts {
  trackMapApi: TrackMapApi;
  chartsRef: any[];
  fullMin: number;
  fullMax: number;
  onZoomChange?: (isZoomed: boolean) => void;
}

function zoomSyncPlugin({ trackMapApi, chartsRef, fullMin, fullMax, onZoomChange }: ZoomPluginOpts) {
  let _syncing = false;
  const maxSampleIdx = Math.round(fullMax / 0.1);

  return {
    hooks: {
      setCursor: [
        (u: any) => {
          if (_syncing || !trackMapApi) return;
          const { left, width } = u.select;
          if (width < 2) return;
          const minTime = u.posToVal(left, 'x');
          const maxTime = u.posToVal(left + width, 'x');
          const minIdx = Math.max(0, Math.round(minTime / 0.1));
          const maxIdx = Math.min(maxSampleIdx, Math.round(maxTime / 0.1));
          trackMapApi.setRange(minIdx, maxIdx);

          _syncing = true;
          try {
            const dpr = window.devicePixelRatio || 1;
            for (const peer of chartsRef) {
              if (!peer || peer === u) continue;
              const peerLeft = Math.round(peer.valToPos(minTime, 'x'));
              const peerRight = Math.round(peer.valToPos(maxTime, 'x'));
              if (peerRight > peerLeft) {
                peer.setSelect({
                  left: peerLeft,
                  width: peerRight - peerLeft,
                  top: 0,
                  height: peer.bbox.height / dpr,
                }, false);
              }
            }
          } finally {
            _syncing = false;
          }
        },
      ],
      setScale: [
        (u: any, scaleKey: string) => {
          if (scaleKey !== 'x' || _syncing) return;
          const { min, max } = u.scales.x;
          const eps = 0.05;
          const isZoomed = (min > fullMin + eps) || (max < fullMax - eps);

          if (trackMapApi) {
            if (isZoomed) {
              trackMapApi.setRange(
                Math.max(0, Math.round(min / 0.1)),
                Math.min(maxSampleIdx, Math.round(max / 0.1)),
              );
            } else {
              trackMapApi.setRange(-1, -1);
            }
          }

          const clearSel = { left: 0, width: 0, top: 0, height: 0 };
          u.setSelect(clearSel, false);
          _syncing = true;
          try {
            for (const peer of chartsRef) {
              if (!peer || peer === u) continue;
              peer.setSelect(clearSel, false);
              peer.setScale('x', { min, max });
            }
          } finally {
            _syncing = false;
          }

          onZoomChange?.(isZoomed);
        },
      ],
    },
  };
}

// ── Sector band plugin ────────────────────────────────────────────────

interface SampleWithSector {
  sector: number;
  [key: string]: any;
}

function sectorBandsPlugin(samples: SampleWithSector[]) {
  const transitions: number[] = [];
  let prevSector: number | null = null;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i].sector;
    if (s !== prevSector) {
      transitions.push(i);
      prevSector = s;
    }
  }

  return {
    hooks: {
      drawSeries: [
        (u: any, si: number) => {
          if (si !== 1) return;
          const ctx = u.ctx;
          const { left, top, width, height } = u.bbox;

          for (let t = 0; t < transitions.length; t++) {
            if (t % 2 !== 1) continue;
            const startIdx = transitions[t];
            const endIdx = t + 1 < transitions.length ? transitions[t + 1] : samples.length - 1;
            const startTime = startIdx * 0.1;
            const endTime = endIdx * 0.1;
            const x0 = Math.max(u.valToPos(startTime, 'x', true), left);
            const x1 = Math.min(u.valToPos(endTime, 'x', true), left + width);

            if (x1 > x0) {
              ctx.fillStyle = CHART_COLORS.sectorBand;
              ctx.fillRect(x0, top, x1 - x0, height);
            }
          }
        },
      ],
    },
  };
}

// ── Shared chart options factory ──────────────────────────────────────

function baseOpts(container: HTMLElement, _title: string) {
  const w = container.clientWidth || container.offsetWidth || 600;
  const sync = getCursorSync();
  return {
    width: w,
    height: 220,
    cursor: {
      sync: sync ? { key: sync.key } : undefined,
      drag: { x: true, y: false },
    },
    legend: { show: true },
    axes: [
      {
        stroke: CHART_COLORS.tick,
        grid: { stroke: CHART_COLORS.grid, width: 1 },
        ticks: { stroke: CHART_COLORS.grid, width: 1 },
        values: (_u: any, vals: number[]) => vals.map(v => v.toFixed(1) + 's'),
        font: '11px Inter',
      },
      {
        stroke: CHART_COLORS.tick,
        grid: { stroke: CHART_COLORS.grid, width: 1 },
        ticks: { stroke: CHART_COLORS.grid, width: 1 },
        font: '11px Inter',
      },
    ],
    scales: {
      x: { time: false },
    },
  };
}

/** Attach a ResizeObserver to keep chart width in sync with its container. */
function autoResize(chart: any, container: HTMLElement) {
  const ro = new ResizeObserver(entries => {
    for (const entry of entries) {
      const w = Math.floor(entry.contentRect.width);
      if (w > 0 && w !== chart.width) {
        chart.setSize({ width: w, height: chart.height });
      }
    }
  });
  ro.observe(container);
  return ro;
}

// ── Speed Chart ───────────────────────────────────────────────────────

export function createSpeedChart(
  container: HTMLElement,
  samples: SampleWithSector[],
  trackMapApi: TrackMapApi | null = null,
  zoomPluginOpts: ZoomPluginOpts | null = null,
  pinnedPlugin: any = null,
) {
  if (!uPlot) return null;
  const times = samples.map((_s: any, i: number) => i * 0.1);
  const speeds = samples.map((s: any) => s.speedGpsRaw / 10);

  const plugins: any[] = [sectorBandsPlugin(samples), cursorTrackPlugin(trackMapApi)];
  if (zoomPluginOpts) plugins.push(zoomSyncPlugin(zoomPluginOpts));
  if (pinnedPlugin) plugins.push(pinnedPlugin);

  const opts = {
    ...baseOpts(container, 'Speed'),
    series: [
      {},
      {
        label: 'Speed',
        stroke: CHART_COLORS.speed,
        width: 1.5,
        fill: hexToFill(CHART_COLORS.speed, 0.08),
      },
    ],
    plugins,
  };

  const chart = new uPlot(opts, [times, speeds], container);
  autoResize(chart, container);
  return chart;
}

// ── Acceleration Chart ────────────────────────────────────────────────

export function createAccelerationChart(
  container: HTMLElement,
  samples: SampleWithSector[],
  trackMapApi: TrackMapApi | null = null,
  zoomPluginOpts: ZoomPluginOpts | null = null,
  pinnedPlugin: any = null,
) {
  if (!uPlot) return null;
  const times = samples.map((_s: any, i: number) => i * 0.1);
  const accel = samples.map((s: any) => s.acceleration ?? 0);

  const plugins: any[] = [sectorBandsPlugin(samples), cursorTrackPlugin(trackMapApi)];
  if (zoomPluginOpts) plugins.push(zoomSyncPlugin(zoomPluginOpts));
  if (pinnedPlugin) plugins.push(pinnedPlugin);

  const opts = {
    ...baseOpts(container, 'Acceleration'),
    series: [
      {},
      {
        label: 'Acceleration',
        stroke: CHART_COLORS.acceleration,
        width: 1.5,
        fill: hexToFill(CHART_COLORS.acceleration, 0.08),
      },
    ],
    plugins,
  };

  const chart = new uPlot(opts, [times, accel], container);
  autoResize(chart, container);
  return chart;
}

// ── RPM Chart ─────────────────────────────────────────────────────────

export function createRpmChart(
  container: HTMLElement,
  samples: SampleWithSector[],
  trackMapApi: TrackMapApi | null = null,
  zoomPluginOpts: ZoomPluginOpts | null = null,
  pinnedPlugin: any = null,
) {
  if (!uPlot) return null;
  const times = samples.map((_s: any, i: number) => i * 0.1);
  const rpms = samples.map((s: any) => s.rpm);

  if (rpms.every((v: number) => !v)) {
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'center';
    container.style.height = '220px';
    container.style.color = 'var(--color-text-muted, #666)';
    container.style.fontSize = '13px';
    container.textContent = 'RPM data not available';
    return null;
  }

  const plugins: any[] = [sectorBandsPlugin(samples), cursorTrackPlugin(trackMapApi)];
  if (zoomPluginOpts) plugins.push(zoomSyncPlugin(zoomPluginOpts));
  if (pinnedPlugin) plugins.push(pinnedPlugin);

  const opts = {
    ...baseOpts(container, 'RPM'),
    series: [
      {},
      {
        label: 'RPM',
        stroke: CHART_COLORS.rpm,
        width: 1.5,
        fill: hexToFill(CHART_COLORS.rpm, 0.08),
      },
    ],
    plugins,
  };

  const chart = new uPlot(opts, [times, rpms], container);
  autoResize(chart, container);
  return chart;
}

// ── G-Force Chart ─────────────────────────────────────────────────────

export function createGForceChart(
  container: HTMLElement,
  samples: SampleWithSector[],
  trackMapApi: TrackMapApi | null = null,
  zoomPluginOpts: ZoomPluginOpts | null = null,
  pinnedPlugin: any = null,
) {
  if (!uPlot) return null;
  const times = samples.map((_s: any, i: number) => i * 0.1);
  const gx = samples.map((s: any) => (s.gforceXRaw - 1000) / 100);
  const gy = samples.map((s: any) => -(s.gforceYRaw - 1000) / 100);

  const plugins: any[] = [sectorBandsPlugin(samples), cursorTrackPlugin(trackMapApi)];
  if (zoomPluginOpts) plugins.push(zoomSyncPlugin(zoomPluginOpts));
  if (pinnedPlugin) plugins.push(pinnedPlugin);

  const opts = {
    ...baseOpts(container, 'G-Force'),
    series: [
      {},
      {
        label: 'Lateral (X)',
        stroke: CHART_COLORS.gforceX,
        width: 1.5,
      },
      {
        label: 'Longitudinal (Y)',
        stroke: CHART_COLORS.gforceY,
        width: 1.5,
      },
    ],
    plugins,
  };

  const chart = new uPlot(opts, [times, gx, gy], container);
  autoResize(chart, container);
  return chart;
}

// ── Sector Highlight Plugin ───────────────────────────────────────────

function sectorHighlightPlugin(bestIdx: number, worstIdx: number) {
  return {
    hooks: {
      draw: [
        (u: any) => {
          const ctx = u.ctx;
          function drawDot(idx: number, color: string) {
            if (idx < 0) return;
            const xVal = u.data[0][idx];
            const yVal = u.data[1][idx];
            if (xVal == null || yVal == null) return;
            const cx = u.valToPos(xVal, 'x', true);
            const cy = u.valToPos(yVal, 'y', true);
            ctx.beginPath();
            ctx.arc(cx, cy, 8, 0, Math.PI * 2);
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            ctx.fillStyle = `rgba(${r},${g},${b},0.3)`;
            ctx.fill();
            ctx.beginPath();
            ctx.arc(cx, cy, 4, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
          }
          drawDot(bestIdx, '#10b981');
          drawDot(worstIdx, '#f87171');
        },
      ],
    },
  };
}

// ── Generic Metric Timeline Chart ─────────────────────────────────────

export interface MetricChartOptions {
  label: string;
  color: string;
  yLabel: string;
  yFormat: (v: number) => string;
  reverseGoodness: boolean;
}

export function createMetricTimelineChart(
  container: HTMLElement,
  yData: number[],
  sessionBoundaries: number[],
  options: MetricChartOptions,
) {
  if (!uPlot || !yData || yData.length === 0) return null;
  const xData = yData.map((_: any, i: number) => i + 1);

  const validY = yData.filter(v => v != null && v > 0);
  const avgVal = validY.length > 0 ? validY.reduce((a, b) => a + b, 0) / validY.length : 0;

  let bestIdx = -1, worstIdx = -1;
  for (let i = 0; i < yData.length; i++) {
    if (yData[i] > 0) {
      if (bestIdx < 0) {
        bestIdx = i; worstIdx = i;
      } else {
        if (options.reverseGoodness) {
          if (yData[i] > yData[bestIdx]) bestIdx = i;
          if (yData[i] < yData[worstIdx]) worstIdx = i;
        } else {
          if (yData[i] < yData[bestIdx]) bestIdx = i;
          if (yData[i] > yData[worstIdx]) worstIdx = i;
        }
      }
    }
  }

  const w = container.clientWidth || container.offsetWidth || 600;
  const color = options.color || '#10b981';
  const avgLabel = 'AVG ' + options.yFormat(avgVal);

  const overlaysPlugin = {
    hooks: {
      draw: [(u: any) => {
        const ctx = u.ctx;
        const { left, top, width, height } = u.bbox;

        if (avgVal > 0) {
          const yPos = u.valToPos(avgVal, 'y', true);
          if (yPos >= top && yPos <= top + height) {
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([6, 4]);
            ctx.strokeStyle = 'rgba(96, 165, 250, 0.4)';
            ctx.lineWidth = 1;
            ctx.moveTo(left, yPos);
            ctx.lineTo(left + width, yPos);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.font = '500 9px Inter, sans-serif';
            ctx.fillStyle = 'rgba(96, 165, 250, 0.6)';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';
            ctx.fillText(avgLabel, left + width - 4, yPos - 3);
            ctx.restore();
          }
        }

        if (sessionBoundaries && sessionBoundaries.length > 0) {
          ctx.save();
          ctx.setLineDash([3, 3]);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
          ctx.lineWidth = 1;
          for (const bIdx of sessionBoundaries) {
            const xPos = u.valToPos(bIdx, 'x', true);
            if (xPos >= left && xPos <= left + width) {
              ctx.beginPath();
              ctx.moveTo(xPos, top);
              ctx.lineTo(xPos, top + height);
              ctx.stroke();
            }
          }
          ctx.setLineDash([]);
          ctx.restore();
        }
      }],
    },
  };

  const opts = {
    width: w,
    height: 260,
    cursor: {
      drag: { x: false, y: false },
      select: { show: false },
    },
    legend: { show: true },
    axes: [
      {
        stroke: CHART_COLORS.tick,
        grid: { stroke: CHART_COLORS.grid, width: 1 },
        ticks: { stroke: CHART_COLORS.grid, width: 1 },
        values: (_u: any, vals: number[]) => vals.map(v => v.toFixed(0)),
        label: 'Lap',
        labelFont: '500 11px Inter',
        font: '11px Inter',
        labelGap: 6,
      },
      {
        stroke: CHART_COLORS.tick,
        grid: { stroke: CHART_COLORS.grid, width: 1 },
        ticks: { stroke: CHART_COLORS.grid, width: 1 },
        label: options.yLabel,
        labelFont: '500 11px Inter',
        font: '11px Inter',
        labelGap: 6,
        values: (_u: any, vals: number[]) => vals.map(v => options.yFormat(v)),
      },
    ],
    scales: { x: { time: false } },
    series: [
      {},
      {
        label: options.label,
        stroke: color,
        width: 2,
        fill: hexToFill(color, 0.06),
        points: { show: true, size: 5, fill: color, stroke: color },
      },
    ],
    plugins: [
      overlaysPlugin,
      sectorHighlightPlugin(bestIdx, worstIdx),
    ],
  };

  const chart = new uPlot(opts, [xData, yData], container);
  autoResize(chart, container);
  return chart;
}

// ── Sector-specific chart creators ────────────────────────────────────

import type { SectorLapResult, AccelerationLapResult, SectorSessionMedian } from '@/types/telemetry';

export function createSectorTimelineChart(
  container: HTMLElement,
  sectorResults: SectorLapResult[],
  sessionBoundaries: number[],
) {
  if (!sectorResults || sectorResults.length === 0) return null;
  const yData = sectorResults.map(r => r.sectorDurationMs / 1000);
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Sector Time',
    color: '#10b981',
    yLabel: 'Duration (s)',
    yFormat: (v) => v.toFixed(3) + 's',
    reverseGoodness: false,
  });
}

export function createSectorMaxSpeedChart(
  container: HTMLElement,
  sectorResults: SectorLapResult[],
  sessionBoundaries: number[],
) {
  if (!sectorResults || sectorResults.length === 0) return null;
  const yData = sectorResults.map(r => (r.maxSpeedRaw || 0) / 10);
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Top Speed',
    color: '#34d399',
    yLabel: 'km/h',
    yFormat: (v) => v.toFixed(0) + ' km/h',
    reverseGoodness: true,
  });
}

export function createSectorMinSpeedChart(
  container: HTMLElement,
  sectorResults: SectorLapResult[],
  sessionBoundaries: number[],
) {
  if (!sectorResults || sectorResults.length === 0) return null;
  const yData = sectorResults.map(r => (r.minSpeedRaw || 0) / 10);
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Min Speed',
    color: '#60a5fa',
    yLabel: 'km/h',
    yFormat: (v) => v.toFixed(0) + ' km/h',
    reverseGoodness: true,
  });
}

export function createSectorMaxRpmChart(
  container: HTMLElement,
  sectorResults: SectorLapResult[],
  sessionBoundaries: number[],
) {
  if (!sectorResults || sectorResults.length === 0) return null;
  const yData = sectorResults.map(r => r.maxRpm || 0);
  if (yData.every(v => !v)) return null;
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Max RPM',
    color: '#fbbf24',
    yLabel: 'RPM',
    yFormat: (v) => v.toFixed(0),
    reverseGoodness: true,
  });
}

export function createSectorMinRpmChart(
  container: HTMLElement,
  sectorResults: SectorLapResult[],
  sessionBoundaries: number[],
) {
  if (!sectorResults || sectorResults.length === 0) return null;
  const yData = sectorResults.map(r => r.minRpm || 0);
  if (yData.every(v => !v)) return null;
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Min RPM',
    color: '#f87171',
    yLabel: 'RPM',
    yFormat: (v) => v.toFixed(0),
    reverseGoodness: true,
  });
}

// ── Session Median Chart ──────────────────────────────────────────────

export function createSessionMedianChart(
  container: HTMLElement,
  sessionMedians: SectorSessionMedian[],
  extractor: (m: SectorSessionMedian) => number,
  sessionMeta: Array<{ id: string; date?: string; time?: string }>,
  options: MetricChartOptions,
) {
  if (!uPlot || !sessionMedians || sessionMedians.length === 0) return null;

  const metaMap: Record<string, any> = {};
  (sessionMeta || []).forEach(s => { metaMap[s.id] = s; });

  const xLabels: string[] = [];
  const xData: number[] = [];
  const yData: number[] = [];
  sessionMedians.forEach((m, idx) => {
    const meta = metaMap[m.sessionId];
    const dateStr = meta?.date || m.sessionDate || '';
    const timeStr = meta?.time || m.sessionTime || '';
    const label = `${dateStr} ${timeStr}`.trim() || m.sessionId;
    xLabels.push(label);
    xData.push(idx + 1);
    const val = extractor(m);
    yData.push(val != null && val > 0 ? val : 0);
  });

  if (yData.every(v => v === 0)) return null;

  let bestIdx = -1, worstIdx = -1;
  for (let i = 0; i < yData.length; i++) {
    if (yData[i] <= 0) continue;
    if (bestIdx < 0) { bestIdx = i; worstIdx = i; continue; }
    if (options.reverseGoodness) {
      if (yData[i] > yData[bestIdx]) bestIdx = i;
      if (yData[i] < yData[worstIdx]) worstIdx = i;
    } else {
      if (yData[i] < yData[bestIdx]) bestIdx = i;
      if (yData[i] > yData[worstIdx]) worstIdx = i;
    }
  }

  const color = options.color || '#10b981';
  const w = container.clientWidth || container.offsetWidth || 600;

  const avgVal = yData.filter(v => v > 0).reduce((a, b) => a + b, 0) / yData.filter(v => v > 0).length;
  const avgLabel = 'AVG ' + options.yFormat(avgVal);

  const barsPlugin = {
    hooks: {
      draw: [(u: any) => {
        const ctx = u.ctx;
        const { left, top, width, height } = u.bbox;
        const barW = Math.max(6, Math.min(40, width / (xData.length * 2)));

        for (let i = 0; i < xData.length; i++) {
          const xPos = u.valToPos(xData[i], 'x', true);
          const yVal = yData[i];
          if (yVal <= 0) continue;
          const yPos = u.valToPos(yVal, 'y', true);
          const yBase = u.valToPos(u.scales.y.min, 'y', true);

          let barColor = color;
          if (i === bestIdx) barColor = '#10b981';
          if (i === worstIdx) barColor = '#f87171';

          const r = parseInt(barColor.slice(1, 3), 16);
          const g = parseInt(barColor.slice(3, 5), 16);
          const b = parseInt(barColor.slice(5, 7), 16);

          ctx.fillStyle = `rgba(${r},${g},${b},0.25)`;
          ctx.fillRect(xPos - barW / 2, yPos, barW, yBase - yPos);
          ctx.fillStyle = barColor;
          ctx.fillRect(xPos - barW / 2, yPos - 2, barW, 4);

          ctx.beginPath();
          ctx.arc(xPos, yPos, i === bestIdx || i === worstIdx ? 5 : 4, 0, Math.PI * 2);
          ctx.fillStyle = barColor;
          ctx.fill();

          if (i === bestIdx || i === worstIdx) {
            ctx.beginPath();
            ctx.arc(xPos, yPos, 9, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(${r},${g},${b},0.25)`;
            ctx.fill();
          }
        }

        if (avgVal > 0) {
          const yAvgPos = u.valToPos(avgVal, 'y', true);
          if (yAvgPos >= top && yAvgPos <= top + height) {
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([6, 4]);
            ctx.strokeStyle = 'rgba(96, 165, 250, 0.4)';
            ctx.lineWidth = 1;
            ctx.moveTo(left, yAvgPos);
            ctx.lineTo(left + width, yAvgPos);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.font = '500 9px Inter, sans-serif';
            ctx.fillStyle = 'rgba(96, 165, 250, 0.6)';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';
            ctx.fillText(avgLabel, left + width - 4, yAvgPos - 3);
            ctx.restore();
          }
        }
      }],
    },
  };

  const opts = {
    width: w,
    height: 200,
    cursor: {
      drag: { x: false, y: false },
      select: { show: false },
      points: { show: false },
    },
    legend: { show: false },
    axes: [
      {
        stroke: CHART_COLORS.tick,
        grid: { stroke: CHART_COLORS.grid, width: 1 },
        ticks: { stroke: CHART_COLORS.grid, width: 1 },
        label: 'Session',
        labelFont: '500 11px Inter',
        font: '11px Inter',
        labelGap: 6,
        values: (_u: any, vals: number[]) => vals.map(v => {
          const idx = Math.round(v) - 1;
          if (idx < 0 || idx >= xLabels.length) return '';
          const lbl = xLabels[idx] || '';
          return lbl.length > 5 ? lbl.slice(-5) : lbl;
        }),
        splits: xData,
      },
      {
        stroke: CHART_COLORS.tick,
        grid: { stroke: CHART_COLORS.grid, width: 1 },
        ticks: { stroke: CHART_COLORS.grid, width: 1 },
        label: options.yLabel,
        labelFont: '500 11px Inter',
        font: '11px Inter',
        labelGap: 6,
        values: (_u: any, vals: number[]) => vals.map(v => options.yFormat(v)),
      },
    ],
    scales: {
      x: { time: false, range: [0.5, xData.length + 0.5] },
      y: {
        range: (_u: any, dmin: number, dmax: number) => {
          const pad = (dmax - dmin) * 0.1 || dmax * 0.05 || 1;
          return [Math.max(0, dmin - pad), dmax + pad];
        },
      },
    },
    series: [
      {},
      {
        label: options.label,
        stroke: 'transparent',
        width: 0,
        points: { show: false },
      },
    ],
    plugins: [barsPlugin],
  };

  const chart = new uPlot(opts, [xData, yData], container);
  autoResize(chart, container);
  return chart;
}

// ── Acceleration-specific chart creators ──────────────────────────────

export function createAccelDurationChart(
  container: HTMLElement,
  laps: AccelerationLapResult[],
  sessionBoundaries: number[],
) {
  if (!laps || laps.length === 0) return null;
  const yData = laps.map(r => r.durationMs / 1000);
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Duration',
    color: '#10b981',
    yLabel: 'Duration (s)',
    yFormat: (v) => v.toFixed(3) + 's',
    reverseGoodness: false,
  });
}

export function createAccelTopSpeedChart(
  container: HTMLElement,
  laps: AccelerationLapResult[],
  sessionBoundaries: number[],
) {
  if (!laps || laps.length === 0) return null;
  const yData = laps.map(r => (r.maxSpeedRaw || 0) / 10);
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Top Speed',
    color: '#34d399',
    yLabel: 'km/h',
    yFormat: (v) => v.toFixed(0) + ' km/h',
    reverseGoodness: true,
  });
}

export function createAccelMinSpeedChart(
  container: HTMLElement,
  laps: AccelerationLapResult[],
  sessionBoundaries: number[],
) {
  if (!laps || laps.length === 0) return null;
  const yData = laps.map(r => (r.minSpeedRaw || 0) / 10);
  return createMetricTimelineChart(container, yData, sessionBoundaries, {
    label: 'Min Speed',
    color: '#60a5fa',
    yLabel: 'km/h',
    yFormat: (v) => v.toFixed(0) + ' km/h',
    reverseGoodness: true,
  });
}
